import { ReactNode } from 'react';
import { FormGroup, Stack, Typography } from '@mui/material';
import { PriceSuggestionType } from '@retail/products/types';
import { AppTFunction, useAppTFunction } from '@retail/app/i18n';
import { PriceTypeOption } from './PriceTypeOption';

interface Props {
  priceSuggestionTypes: PriceSuggestionType[];
  checkedPriceTypes: PriceSuggestionType[];
  includeMarkup: boolean;
  checkPriceType: (priceType: PriceSuggestionType) => void;
  getPriceTypeValue: (value: PriceSuggestionType) => string;
}

interface PriceTypeGroup {
  titleKey: Parameters<AppTFunction>[0];
  priceTypes: PriceSuggestionType[];
}

const PRICE_TYPE_GROUPS: PriceTypeGroup[] = [
  {
    titleKey: 'priceSuggestion.finfoPriceSuggestion',
    priceTypes: ['FINFO_BASE', 'FINFO_RECOMMENDED'],
  },
  {
    titleKey: 'priceSuggestion.mgSuggestedPrice',
    priceTypes: ['MG_MODERATE', 'MG_CONSERVATIVE', 'MG_AGGRESSIVE'],
  },
  {
    titleKey: 'priceSuggestion.vvsNorway',
    priceTypes: ['MG_VVS'],
  },
  {
    titleKey: 'priceSuggestion.local',
    priceTypes: ['LOCAL'],
  },
  {
    titleKey: 'priceSuggestion.global',
    priceTypes: ['BRAND'],
  },
];

const KNOWN_PRICE_TYPES = new Set<PriceSuggestionType>([
  ...PRICE_TYPE_GROUPS.flatMap((group) => group.priceTypes),
  'MARKUP',
]);

function PriceTypeSection({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <Stack alignContent="flex-start" gap={1}>
      {title && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}
        >
          {title}
        </Typography>
      )}

      <Stack gap={0.75}>{children}</Stack>
    </Stack>
  );
}

export function PriceTypesSelection({
  priceSuggestionTypes,
  checkedPriceTypes,
  includeMarkup,
  checkPriceType,
  getPriceTypeValue,
}: Props) {
  const t = useAppTFunction();

  const renderOption = (priceType: PriceSuggestionType) => (
    <PriceTypeOption
      key={priceType}
      priceType={priceType}
      isAdded={checkedPriceTypes.includes(priceType)}
      checkPriceType={checkPriceType}
      getPriceTypeValue={getPriceTypeValue}
    />
  );

  const unknownPriceTypes = priceSuggestionTypes.filter(
    (priceType) => !KNOWN_PRICE_TYPES.has(priceType)
  );

  return (
    <FormGroup sx={{ gap: 2 }}>
      {PRICE_TYPE_GROUPS.map((group) => {
        const availableTypes = group.priceTypes.filter((priceType) =>
          priceSuggestionTypes.includes(priceType)
        );

        if (availableTypes.length === 0) {
          return null;
        }

        return (
          <PriceTypeSection key={group.titleKey} title={t(group.titleKey)}>
            {availableTypes.map(renderOption)}
          </PriceTypeSection>
        );
      })}

      {includeMarkup && (
        <PriceTypeSection title={t('calculus.markup')}>{renderOption('MARKUP')}</PriceTypeSection>
      )}

      {unknownPriceTypes.length > 0 && (
        <PriceTypeSection>{unknownPriceTypes.map(renderOption)}</PriceTypeSection>
      )}
    </FormGroup>
  );
}
